/**
 * fileUploader.js
 * Handles logo upload functionality including drag and drop,
 * file validation, and preview generation.
 */

class FileUploader {
  constructor(options = {}) {
    // DOM Elements
    this.uploadArea = document.getElementById('upload-area');
    this.uploadPrompt = document.getElementById('upload-prompt');
    this.fileInput = document.getElementById('logo-upload');
    this.previewContainer = document.getElementById('preview-container');
    this.logoPreview = document.getElementById('logo-preview');
    this.removeButton = document.getElementById('remove-logo');
    this.errorContainer = document.getElementById('upload-error');
    
    // Options with defaults
    this.options = {
      maxSizeInMB: options.maxSizeInMB || 5,
      allowedTypes: options.allowedTypes || ['image/png', 'image/jpeg', 'image/svg+xml'],
      allowedExtensions: options.allowedExtensions || ['.png', '.jpg', '.jpeg', '.svg']
    };
    
    // State
    this.uploadedFile = null;
    this.onFileUploadCallback = options.onFileUpload || null;
    this.onFileRemoveCallback = options.onFileRemove || null;
    
    // Initialize
    this.init();
  }
  
  init() {
    // Click to browse
    this.uploadPrompt.addEventListener('click', () => {
      this.fileInput.click();
    });
    
    // Handle file selection
    this.fileInput.addEventListener('change', (event) => {
      if (event.target.files && event.target.files[0]) {
        this.handleFileSelect(event.target.files[0]);
      }
    });
    
    // Drag and drop functionality
    this.uploadArea.addEventListener('dragover', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.uploadArea.classList.add('dragover');
    });
    
    this.uploadArea.addEventListener('dragleave', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.uploadArea.classList.remove('dragover');
    });
    
    this.uploadArea.addEventListener('drop', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.uploadArea.classList.remove('dragover');
      
      if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        this.handleFileSelect(event.dataTransfer.files[0]);
      }
    });
    
    // Remove logo
    this.removeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeFile();
    });
  }
  
  handleFileSelect(file) {
    if (!this.validateFile(file)) {
      return;
    }
    
    this.uploadedFile = file;
    this.showPreview(file);
    
    if (this.onFileUploadCallback) {
      this.onFileUploadCallback(file);
    }
  }
  
  validateFile(file) {
    // Reset error state
    this.hideError();
    
    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = this.options.allowedTypes.includes(file.type) || 
                        this.options.allowedExtensions.includes(fileExtension);
    
    if (!isValidType) {
      this.showError(`Invalid file type. Please upload ${this.options.allowedExtensions.join(', ')}`);
      return false;
    }
    
    // Check file size
    const maxSizeInBytes = this.options.maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      this.showError(`File is too large. Maximum size is ${this.options.maxSizeInMB}MB.`);
      return false;
    }
    
    return true;
  }
  
  showPreview(file) {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      this.logoPreview.src = event.target.result;
      this.uploadPrompt.classList.add('d-none');
      this.previewContainer.classList.remove('d-none');
    };
    
    reader.readAsDataURL(file);
  }
  
  removeFile() {
    this.uploadedFile = null;
    this.logoPreview.src = '';
    this.fileInput.value = '';
    this.uploadPrompt.classList.remove('d-none');
    this.previewContainer.classList.add('d-none');
    
    if (this.onFileRemoveCallback) {
      this.onFileRemoveCallback();
    }
  }
  
  showError(message) {
    this.errorContainer.textContent = message;
    this.errorContainer.classList.remove('d-none');
  }
  
  hideError() {
    this.errorContainer.classList.add('d-none');
  }
  
  getUploadedFile() {
    return this.uploadedFile;
  }
  
  hasUploadedFile() {
    return this.uploadedFile !== null;
  }
}

// Export
window.FileUploader = FileUploader;